/**
 * Забытая раскладка: «шзрщту» — это `iphone`, набранный русской.
 *
 * Приёмщик весь день пишет клиенту по-русски, потом ищет модель — и первые
 * буквы уходят в кириллицу. Список отвечает «ничего не нашлось», хотя запрос
 * верный: не совпала не строка, а раскладка. Переключить и набрать заново
 * человек, конечно, может, но узнаёт он об этом, уже прочитав пустоту.
 *
 * Не путать с `translit()` из `model-norm.ts`. Тот про **звук**: «аспайр» →
 * `aspire`, человек пишет то, что услышал от клиента. Этот — про **клавишу**:
 * буквы разные, палец нажал ту же. Задачи разные, и подменять одну другой
 * нельзя — «аспайр» через раскладку даёт `fcgfqh`, бессмыслицу.
 *
 * Карта — ЙЦУКЕН против QWERTY, по расположению на клавиатуре. Знаки
 * препинания тоже: точка с запятой на русской — это «ж», и в названиях
 * моделей такое встречается.
 */

/** QWERTY → ЙЦУКЕН: что напечатается, если раскладка русская */
const TO_RU: Record<string, string> = {
  q: 'й', w: 'ц', e: 'у', r: 'к', t: 'е', y: 'н', u: 'г', i: 'ш', o: 'щ', p: 'з',
  '[': 'х', ']': 'ъ',
  a: 'ф', s: 'ы', d: 'в', f: 'а', g: 'п', h: 'р', j: 'о', k: 'л', l: 'д',
  ';': 'ж', "'": 'э',
  z: 'я', x: 'ч', c: 'с', v: 'м', b: 'и', n: 'т', m: 'ь',
  ',': 'б', '.': 'ю', '/': '.',
  '`': 'ё',
}

/** Обратная сторона той же карты — строится из неё, чтобы не разошлись */
const TO_EN: Record<string, string> = Object.fromEntries(
  Object.entries(TO_RU).map(([en, ru]) => [ru, en]),
)

/**
 * Переложить строку на другую раскладку.
 *
 * Направление выбирается по самой строке, а не параметром: у кириллицы и
 * латиницы алфавиты не пересекаются, и спрашивать об этом снаружи значит
 * заставить каждое место поиска решать одно и то же.
 *
 * Смешанную строку («айфон 13 pro») переводим целиком по преобладающей
 * стороне: «13» и пробелы в обеих картах отсутствуют и проходят как есть.
 */
export function flipLayout(text: string | null | undefined): string {
  const raw = (text ?? '').toLowerCase()
  if (!raw) return ''

  const cyrillic = (raw.match(/[а-яё]/g) ?? []).length
  const latin = (raw.match(/[a-z]/g) ?? []).length
  if (cyrillic === 0 && latin === 0) return raw

  const map = cyrillic > latin ? TO_EN : TO_RU

  return [...raw].map((sign) => map[sign] ?? sign).join('')
}

/**
 * Варианты запроса: сам он и он же в другой раскладке.
 *
 * Второй вариант появляется, только когда он отличается и не пустой — иначе
 * поиск по цифрам («82000031») искал бы одно и то же дважды.
 *
 * Обе стороны нужны в обе стороны: справочник моделей латиницей, а клиенты и
 * филиалы — кириллицей, и забыть переключиться можно в любую из них.
 */
export function searchVariants(raw: string | null | undefined): string[] {
  const text = (raw ?? '').trim()
  if (!text) return []

  /* С одной буквы не гадаем: «с» на другой раскладке — это `c`, и поиск начал
     бы возвращать всё, где эта буква есть вообще. Пока набран один знак,
     человек ещё печатает, и лишние строки в списке мешают больше, чем
     помогают */
  if (text.length < 2) return [text]

  const flipped = flipLayout(text)
  return flipped && flipped !== text.toLowerCase() ? [text, flipped] : [text]
}
